import { useEffect, useMemo, useState } from "react";
import { Ksef } from "../lib/api";
import { API } from "../lib/api";
import { toast } from "sonner";
import {
  FileStack, Plus, Trash2, Send, Download, FileText, CheckCircle2,
  XCircle, Clock, Loader2, ArrowLeft, AlertCircle, ShieldCheck, ExternalLink,
  UserPlus, RefreshCw, Save, Eye,
} from "lucide-react";
import { useConfirm } from "./ConfirmProvider";
import { useDeepLink } from "../lib/deepLink";

/**
 * Sales-invoices tab for the Accounting page.
 * Three sub-views: list, form (new), detail (view existing).
 */
export default function SalesInvoices() {
  const [view, setView] = useState("list"); // list | new | detail
  const [invoices, setInvoices] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncDays, setSyncDays] = useState(30);
  const { confirm } = useConfirm();

  const refresh = () =>
    Ksef.listInvoices()
      .then(setInvoices)
      .catch((e) => toast.error("Błąd: " + (e.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));

  useEffect(() => {
    refresh();
  }, []);
  useDeepLink("open", !loading, (id) => handleOpen(id));

  const handleSyncFromKsef = async () => {
    setSyncing(true);
    const tid = toast.loading(`Pobieram faktury sprzedaży z KSeF (${syncDays} dni)…`);
    try {
      const r = await Ksef.syncSalesInvoices(syncDays);
      toast.success(
        `KSeF: znalezione ${r.found}, pobrane ${r.downloaded}, pominięte ${r.skipped}, dodane do Finanse ${r.imported_to_finance}`,
        { id: tid, duration: 6000 }
      );
      refresh();
    } catch (e) {
      toast.error("Błąd sync: " + (e.response?.data?.detail || e.message), { id: tid });
    } finally {
      setSyncing(false);
    }
  };

  const handleCreated = async (inv) => {
    setCurrent(inv);
    setView("detail");
    refresh();
    toast.success("Faktura utworzona (draft)");
  };

  const handleOpen = async (id) => {
    try {
      const inv = await Ksef.getInvoice(id);
      setCurrent(inv);
      setView("detail");
    } catch (e) {
      toast.error("Błąd: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Usunąć fakturę? Draft można usunąć. Wysłanej i zaakceptowanej faktury usunąć się nie da.", {
      confirmLabel: "Usuń",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await Ksef.deleteInvoice(id);
      toast.success("Faktura usunięta");
      refresh();
    } catch (e) {
      toast.error("Błąd: " + (e.response?.data?.detail || e.message));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 p-8">
        <Loader2 className="w-4 h-4 animate-spin" /> Ładuję faktury…
      </div>
    );
  }

  if (view === "new") {
    return <InvoiceForm onCancel={() => setView("list")} onCreated={handleCreated} />;
  }
  if (view === "detail" && current) {
    return (
      <InvoiceDetail
        invoice={current}
        onBack={() => {
          setView("list");
          refresh();
        }}
        onReload={async () => {
          const inv = await Ksef.getInvoice(current.id);
          setCurrent(inv);
        }}
      />
    );
  }

  return (
    <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#18181b]">
      <div className="px-5 py-3 border-b-2 border-zinc-900 bg-zinc-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileStack className="w-4 h-4" />
          <h2 className="font-heading font-black uppercase tracking-tight text-base">Lista faktur</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={syncDays}
            onChange={(e) => setSyncDays(parseInt(e.target.value, 10))}
            data-testid="sales-days-select"
            className="text-xs font-mono border-2 border-zinc-900 px-2 py-2"
            title="Zakres pobierania z KSeF"
          >
            <option value={7}>7 dni</option>
            <option value={30}>30 dni</option>
            <option value={90}>90 dni</option>
            <option value={180}>180 dni (6 msc)</option>
            <option value={365}>365 dni (1 rok)</option>
          </select>
          <button
            onClick={handleSyncFromKsef}
            disabled={syncing}
            data-testid="btn-sync-sales"
            title="Pobierz faktury sprzedaży wystawione w KSeF i dodaj do Finanse"
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white disabled:opacity-50"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {syncing ? "Pobieram…" : "Pobierz z KSeF"}
          </button>
          <button
            onClick={() => setView("new")}
            data-testid="btn-new-invoice"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider bg-brand text-white hover:bg-brand-hover"
          >
            <Plus className="w-3.5 h-3.5" /> Nowa faktura
          </button>
        </div>
      </div>
      {invoices.length === 0 ? (
        <div className="p-10 text-center text-zinc-500 text-sm">
          Brak faktur. Kliknij <span className="font-bold">{'"Nowa faktura"'}</span> aby zacząć.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-100 border-b border-zinc-300">
              <tr>
                <th className="text-left px-4 py-2.5 font-mono uppercase text-[10px] tracking-wider text-zinc-500">Numer</th>
                <th className="text-left px-4 py-2.5 font-mono uppercase text-[10px] tracking-wider text-zinc-500">Data</th>
                <th className="text-left px-4 py-2.5 font-mono uppercase text-[10px] tracking-wider text-zinc-500">Nabywca</th>
                <th className="text-right px-4 py-2.5 font-mono uppercase text-[10px] tracking-wider text-zinc-500">Brutto</th>
                <th className="text-left px-4 py-2.5 font-mono uppercase text-[10px] tracking-wider text-zinc-500">Status</th>
                <th className="text-left px-4 py-2.5 font-mono uppercase text-[10px] tracking-wider text-zinc-500">Nr KSeF</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  data-testid={`invoice-row-${inv.invoice_number}`}
                  className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                  onClick={() => handleOpen(inv.id)}
                >
                  <td className="px-4 py-2 font-bold">{inv.invoice_number}</td>
                  <td className="px-4 py-2 text-zinc-600 font-mono text-xs">{inv.issue_date}</td>
                  <td className="px-4 py-2">{inv.buyer?.name || "—"}</td>
                  <td className="px-4 py-2 text-right font-bold font-mono">
                    {inv.total_gross.toFixed(2)} {inv.currency}
                  </td>
                  <td className="px-4 py-2"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-2 font-mono text-xs text-zinc-600">
                    {inv.ksef_number || "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <a
                        href={`${API}/ksef/invoices/${inv.id}/pdf?inline=1`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="p-1.5 text-zinc-500 hover:text-zinc-900 transition-colors"
                        title="Podgląd faktury (PDF)"
                        data-testid={`invoice-preview-${inv.invoice_number}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </a>
                      {inv.status !== "accepted" && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(inv.id);
                          }}
                          className="p-1.5 text-zinc-400 hover:text-red-600 transition-colors"
                          title="Usuń"
                          data-testid={`invoice-delete-${inv.invoice_number}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    draft: { bg: "bg-zinc-200 text-zinc-700", icon: FileText, label: "Draft" },
    sending: { bg: "bg-amber-200 text-amber-900", icon: Loader2, label: "Wysyłam", spin: true },
    accepted: { bg: "bg-emerald-200 text-emerald-900", icon: CheckCircle2, label: "Zaakceptowana" },
    rejected: { bg: "bg-red-200 text-red-900", icon: XCircle, label: "Odrzucona" },
  }[status] || { bg: "bg-zinc-100 text-zinc-500", icon: Clock, label: status };
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${config.bg}`}
      data-testid={`invoice-status-${status}`}
    >
      <Icon className={`w-3 h-3 ${config.spin ? "animate-spin" : ""}`} />
      {config.label}
    </span>
  );
}

// ==========================================================================
// InvoiceForm — new sales invoice
// ==========================================================================

function InvoiceForm({ onCancel, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const [saving, setSaving] = useState(false);
  const [savingBuyer, setSavingBuyer] = useState(false);
  const [savingIban, setSavingIban] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [form, setForm] = useState({
    invoice_number: `FA/${Date.now() % 10000}/${new Date().getFullYear()}`,
    issue_date: today,
    sale_date: today,
    payment_due_date: "",
    payment_method: "przelew",
    bank_account: "",
    currency: "PLN",
    notes: "",
    reverse_charge_eu: false,
    split_payment: false,
    buyer: { name: "", nip: "", no_nip: false, country: "PL", street_hn: "", city: "", zip: "" },
    items: [{ name: "Usługa transportowa", quantity: 1, unit: "usł.", unit_price_net: 0, vat_rate: "23" }],
  });

  const reloadContractors = () =>
    Ksef.listContractors().then(setContractors).catch(() => {});
  const reloadBankAccounts = () =>
    Ksef.listBankAccounts()
      .then((accs) => {
        setBankAccounts(accs);
        // Prefill default IBAN if user hasn't typed one yet
        const def = accs.find((a) => a.is_default);
        if (def) setForm((prev) => (prev.bank_account ? prev : { ...prev, bank_account: def.iban }));
      })
      .catch(() => {});

  useEffect(() => {
    reloadContractors();
    reloadBankAccounts();
  }, []);

  const totals = useMemo(() => {
    let net = 0, vat = 0;
    for (const it of form.items) {
      const nt = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
      net += nt;
      if (/^\d+$/.test(String(it.vat_rate))) vat += nt * (parseInt(it.vat_rate, 10) / 100);
    }
    return { net, vat, gross: net + vat };
  }, [form.items]);

  const setItem = (i, patch) => {
    const items = form.items.slice();
    items[i] = { ...items[i], ...patch };
    setForm({ ...form, items });
  };

  const addItem = () =>
    setForm({
      ...form,
      items: [...form.items, { name: "", quantity: 1, unit: "usł.", unit_price_net: 0, vat_rate: "23" }],
    });

  const removeItem = (i) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, j) => j !== i) });
  };

  const applyContractor = (c) => {
    setForm({ ...form, buyer: { ...form.buyer, ...c, id: undefined } });
  };

  const saveBuyerAsContractor = async () => {
    if (!form.buyer.name.trim()) {
      toast.error("Wpisz najpierw nazwę nabywcy");
      return;
    }
    setSavingBuyer(true);
    try {
      const saved = await Ksef.saveContractor({
        ...form.buyer,
        nip: form.buyer.nip.replace(/[^0-9]/g, ""),
      });
      toast.success(`Nabywca zapisany w książce (${saved.name})`);
      reloadContractors();
    } catch (e) {
      toast.error("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingBuyer(false);
    }
  };

  const saveIbanToBook = async () => {
    const cleaned = form.bank_account.replace(/\s+/g, "");
    if (!/^[A-Z]{0,2}\d{20,28}$/.test(cleaned)) {
      toast.error("Wpisz najpierw poprawny numer konta (IBAN)");
      return;
    }
    setSavingIban(true);
    try {
      const saved = await Ksef.saveBankAccount({
        iban: cleaned,
        label: "",
        is_default: bankAccounts.length === 0,
      });
      toast.success(saved.is_default ? "Konto zapisane jako domyślne" : "Konto zapisane w książce");
      reloadBankAccounts();
    } catch (e) {
      toast.error("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setSavingIban(false);
    }
  };

  const submit = async () => {
    if (!form.buyer.name.trim()) {
      toast.error("Wpisz nazwę nabywcy");
      return;
    }
    if (!form.buyer.no_nip && !/^\d{10}$/.test(form.buyer.nip.replace(/[^0-9]/g, ""))) {
      toast.error('Nabywca musi mieć NIP (10 cyfr) lub zaznacz "Osoba prywatna"');
      return;
    }
    for (const it of form.items) {
      if (!it.name.trim()) {
        toast.error("Każda pozycja musi mieć nazwę");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        buyer: { ...form.buyer, nip: form.buyer.nip.replace(/[^0-9]/g, "") },
      };
      const inv = await Ksef.createInvoice(payload);
      onCreated(inv);
    } catch (e) {
      toast.error("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#18181b]">
      <div className="px-5 py-3 border-b-2 border-zinc-900 bg-zinc-50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={onCancel}
            className="p-1 hover:bg-zinc-200"
            data-testid="btn-invoice-cancel"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h2 className="font-heading font-black uppercase tracking-tight text-base">Nowa faktura sprzedaży</h2>
        </div>
        <button
          onClick={submit}
          disabled={saving}
          data-testid="btn-invoice-save"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          {saving ? "Zapisuję…" : "Utwórz draft"}
        </button>
      </div>

      <div className="p-5 space-y-6">
        {/* Header row: number + dates + payment */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Numer faktury *">
            <input
              value={form.invoice_number}
              onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
              data-testid="inv-number"
              className="w-full border border-zinc-300 px-3 py-2 font-mono text-sm focus:outline-none focus:border-zinc-900"
            />
          </Field>
          <Field label="Data wystawienia *">
            <input type="date" value={form.issue_date}
                   onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                   className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900" />
          </Field>
          <Field label="Data sprzedaży">
            <input type="date" value={form.sale_date}
                   onChange={(e) => setForm({ ...form, sale_date: e.target.value })}
                   className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900" />
          </Field>
          <Field label="Termin płatności">
            <input type="date" value={form.payment_due_date}
                   onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })}
                   className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900" />
          </Field>
        </div>

        {/* Buyer */}
        <div className="border border-zinc-300 p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Nabywca</h3>
            <div className="flex items-center gap-2">
              {contractors.length > 0 && (
                <select
                  onChange={(e) => {
                    const c = contractors.find((x) => x.id === e.target.value);
                    if (c) applyContractor(c);
                  }}
                  className="text-xs border border-zinc-300 px-2 py-1"
                  defaultValue=""
                  data-testid="inv-contractor-picker"
                >
                  <option value="">↻ Wybierz z bazy…</option>
                  {contractors.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} {c.nip ? `(${c.nip})` : ""}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={saveBuyerAsContractor}
                disabled={savingBuyer || !form.buyer.name.trim()}
                data-testid="btn-save-buyer"
                title="Zapisz aktualne dane nabywcy w książce kontrahentów"
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold uppercase border border-zinc-900 hover:bg-zinc-900 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {savingBuyer ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                Zapisz nabywcę
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Nazwa *" className="md:col-span-2">
              <input value={form.buyer.name}
                     onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, name: e.target.value } })}
                     data-testid="inv-buyer-name"
                     className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900" />
            </Field>
            <Field label="NIP">
              <input value={form.buyer.nip} disabled={form.buyer.no_nip} maxLength={13}
                     onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, nip: e.target.value } })}
                     data-testid="inv-buyer-nip"
                     className="w-full border border-zinc-300 px-3 py-2 font-mono text-sm focus:outline-none focus:border-zinc-900 disabled:bg-zinc-100" />
            </Field>
            <Field label="Kraj">
              <input value={form.buyer.country} maxLength={2}
                     onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, country: e.target.value.toUpperCase() } })}
                     className="w-full border border-zinc-300 px-3 py-2 font-mono text-sm focus:outline-none focus:border-zinc-900 uppercase" />
            </Field>
            <Field label="Ulica i nr" className="md:col-span-2">
              <input value={form.buyer.street_hn}
                     onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, street_hn: e.target.value } })}
                     className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900" />
            </Field>
            <Field label="Kod pocztowy">
              <input value={form.buyer.zip}
                     onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, zip: e.target.value } })}
                     className="w-full border border-zinc-300 px-3 py-2 font-mono text-sm focus:outline-none focus:border-zinc-900" />
            </Field>
            <Field label="Miasto">
              <input value={form.buyer.city}
                     onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, city: e.target.value } })}
                     className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900" />
            </Field>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
            <input type="checkbox" checked={form.buyer.no_nip}
                   onChange={(e) => setForm({ ...form, buyer: { ...form.buyer, no_nip: e.target.checked, nip: e.target.checked ? "" : form.buyer.nip } })}
                   className="accent-brand" />
            Osoba prywatna (brak NIP)
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-zinc-600 cursor-pointer ml-4">
            <input type="checkbox" checked={form.reverse_charge_eu}
                   onChange={(e) => setForm({ ...form, reverse_charge_eu: e.target.checked })}
                   className="accent-brand"
                   data-testid="inv-reverse-charge" />
            Odwrotne obciążenie (UE, art. 28b)
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-zinc-600 cursor-pointer ml-4">
            <input type="checkbox" checked={form.split_payment}
                   onChange={(e) => setForm({ ...form, split_payment: e.target.checked })}
                   className="accent-brand"
                   data-testid="inv-split-payment" />
            Mechanizm podzielonej płatności (MPP)
          </label>
        </div>

        {/* Items */}
        <div className="border border-zinc-300 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Pozycje faktury</h3>
            <button onClick={addItem} data-testid="btn-add-item"
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase border border-zinc-300 hover:bg-zinc-50">
              <Plus className="w-3 h-3" /> Dodaj pozycję
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left pb-1">Nazwa</th>
                  <th className="text-right pb-1 w-20">Ilość</th>
                  <th className="text-left pb-1 w-16">J.m.</th>
                  <th className="text-right pb-1 w-28">Cena netto</th>
                  <th className="text-left pb-1 w-20">Stawka</th>
                  <th className="text-right pb-1 w-28">Netto</th>
                  <th className="text-right pb-1 w-28">Brutto</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => {
                  const netto = Number(it.quantity || 0) * Number(it.unit_price_net || 0);
                  const brutto = /^\d+$/.test(String(it.vat_rate))
                    ? netto * (1 + parseInt(it.vat_rate, 10) / 100)
                    : netto;
                  return (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="py-1">
                        <input value={it.name} onChange={(e) => setItem(i, { name: e.target.value })}
                               data-testid={`item-name-${i}`}
                               className="w-full border-0 border-b border-transparent focus:border-zinc-900 focus:outline-none px-1 py-0.5" />
                      </td>
                      <td className="py-1"><input type="number" step="0.01" value={it.quantity}
                             onChange={(e) => setItem(i, { quantity: e.target.value })}
                             className="w-full text-right border-0 border-b border-transparent focus:border-zinc-900 focus:outline-none px-1 py-0.5" /></td>
                      <td className="py-1"><input value={it.unit} onChange={(e) => setItem(i, { unit: e.target.value })}
                             className="w-full border-0 border-b border-transparent focus:border-zinc-900 focus:outline-none px-1 py-0.5" /></td>
                      <td className="py-1"><input type="number" step="0.01" value={it.unit_price_net}
                             onChange={(e) => setItem(i, { unit_price_net: e.target.value })}
                             data-testid={`item-price-${i}`}
                             className="w-full text-right border-0 border-b border-transparent focus:border-zinc-900 focus:outline-none px-1 py-0.5" /></td>
                      <td className="py-1">
                        <select value={it.vat_rate} onChange={(e) => setItem(i, { vat_rate: e.target.value })}
                                className="w-full border border-zinc-300 px-1 py-0.5 text-xs">
                          <option value="23">23%</option>
                          <option value="8">8%</option>
                          <option value="5">5%</option>
                          <option value="0">0%</option>
                          <option value="zw">zw</option>
                          <option value="np">np</option>
                          <option value="oo">oo</option>
                        </select>
                      </td>
                      <td className="py-1 text-right font-bold font-mono">{netto.toFixed(2)}</td>
                      <td className="py-1 text-right font-bold font-mono">{brutto.toFixed(2)}</td>
                      <td className="py-1">
                        {form.items.length > 1 && (
                          <button onClick={() => removeItem(i)} className="p-1 text-zinc-400 hover:text-red-600">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-8 pt-2 border-t border-zinc-200 text-sm">
            <div>
              <div className="text-[10px] font-mono uppercase text-zinc-500">Netto</div>
              <div className="font-bold font-mono text-right">{totals.net.toFixed(2)} {form.currency}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase text-zinc-500">VAT</div>
              <div className="font-bold font-mono text-right">{totals.vat.toFixed(2)} {form.currency}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase text-zinc-500">Brutto</div>
              <div className="font-black font-mono text-lg text-right">{totals.gross.toFixed(2)} {form.currency}</div>
            </div>
          </div>
        </div>

        {/* Payment + notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Forma płatności">
            <select value={form.payment_method}
                    onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                    className="w-full border border-zinc-300 px-3 py-2 text-sm">
              <option value="przelew">Przelew</option>
              <option value="gotówka">Gotówka</option>
              <option value="karta">Karta</option>
              <option value="blik">BLIK</option>
            </select>
          </Field>
          <Field label="Rachunek bankowy (IBAN)">
            <div className="flex items-center gap-1">
              <input value={form.bank_account}
                     onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                     placeholder="PL00 0000 0000 0000 0000 0000 0000"
                     data-testid="inv-bank-account"
                     className="flex-1 border border-zinc-300 px-3 py-2 font-mono text-xs focus:outline-none focus:border-zinc-900" />
              {bankAccounts.length > 0 && (
                <select
                  onChange={(e) => {
                    const a = bankAccounts.find((x) => x.id === e.target.value);
                    if (a) setForm({ ...form, bank_account: a.iban });
                  }}
                  defaultValue=""
                  title="Wybierz z zapisanych kont"
                  className="text-xs border border-zinc-300 px-1 py-2 max-w-[36px]"
                  data-testid="inv-iban-picker"
                >
                  <option value="">↻</option>
                  {bankAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {(a.label || a.iban)} {a.is_default ? "★" : ""}
                    </option>
                  ))}
                </select>
              )}
              <button
                type="button"
                onClick={saveIbanToBook}
                disabled={savingIban || !form.bank_account}
                data-testid="btn-save-iban"
                title="Zapisz numer konta w książce"
                className="inline-flex items-center justify-center w-9 h-9 border border-zinc-900 hover:bg-zinc-900 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {savingIban ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              </button>
            </div>
          </Field>
          <Field label="Uwagi" className="md:col-span-2">
            <textarea value={form.notes} rows={2}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900 resize-none" />
          </Field>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-mono uppercase tracking-wider text-zinc-500 mb-1">{label}</span>
      {children}
    </label>
  );
}

// ==========================================================================
// InvoiceDetail — view + send + download actions
// ==========================================================================

function InvoiceDetail({ invoice, onBack, onReload }) {
  const [sending, setSending] = useState(false);
  const canSend = invoice.status === "draft" || invoice.status === "rejected";
  const downloadUrl = (kind) => `${API}/ksef/invoices/${invoice.id}/${kind}`;

  const send = async () => {
    setSending(true);
    const tid = toast.loading("Wysyłam do KSeF (auth → sesja → wysyłka → close)…");
    try {
      const r = await Ksef.sendInvoice(invoice.id);
      if (r.ksef_number) {
        toast.success(`KSeF przyjął fakturę: ${r.ksef_number}`, { id: tid });
      } else {
        toast.error("KSeF odrzucił fakturę: " + (r.message || "brak Numeru KSeF"), { id: tid });
      }
      onReload();
    } catch (e) {
      toast.error("Błąd: " + (e.response?.data?.detail || e.message), { id: tid });
      onReload();
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white border-2 border-zinc-900 shadow-[6px_6px_0_0_#18181b]">
      <div className="px-5 py-3 border-b-2 border-zinc-900 bg-zinc-50 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1 hover:bg-zinc-200">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h2 className="font-heading font-black uppercase tracking-tight text-base">
            {invoice.invoice_number}
          </h2>
          <StatusBadge status={invoice.status} />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a href={`${API}/ksef/invoices/${invoice.id}/pdf?inline=1`} target="_blank" rel="noreferrer"
             data-testid="btn-preview-invoice"
             className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase border-2 border-brand text-brand hover:bg-brand hover:text-white">
            <Eye className="w-3 h-3" /> Podgląd
          </a>
          <a href={downloadUrl("pdf")} target="_blank" rel="noreferrer"
             data-testid="btn-download-pdf"
             className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white">
            <Download className="w-3 h-3" /> PDF
          </a>
          <a href={downloadUrl("xml")} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white">
            <FileText className="w-3 h-3" /> XML
          </a>
          {invoice.ksef_upo_available && (
            <a href={downloadUrl("upo")} target="_blank" rel="noreferrer"
               data-testid="btn-download-upo"
               className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase border-2 border-emerald-700 text-emerald-900 hover:bg-emerald-700 hover:text-white">
              <ShieldCheck className="w-3 h-3" /> UPO
            </a>
          )}
          {canSend && (
            <button onClick={send} disabled={sending}
                    data-testid="btn-send-invoice"
                    className="inline-flex items-center gap-1 px-4 py-2 text-xs font-bold uppercase bg-brand text-white hover:bg-brand-hover disabled:opacity-50">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              {sending ? "Wysyłam…" : "Wyślij do KSeF"}
            </button>
          )}
        </div>
      </div>

      <div className="p-5 space-y-4">
        {invoice.ksef_number && (
          <div className="border-2 border-emerald-600 bg-emerald-50 p-3 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-700" />
            <div className="text-sm">
              <div className="font-bold text-emerald-900">Numer KSeF: <span className="font-mono">{invoice.ksef_number}</span></div>
              <div className="text-xs text-emerald-700">
                Wysłano: {invoice.ksef_sent_at?.slice(0, 19).replace("T", " ")}
              </div>
            </div>
          </div>
        )}
        {invoice.ksef_error && (
          <div className="border-2 border-red-600 bg-red-50 p-3 text-sm text-red-900">
            <div className="font-bold flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Błąd KSeF</div>
            <div className="text-xs mt-1 whitespace-pre-wrap break-all font-mono">{invoice.ksef_error}</div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-bold text-xs uppercase tracking-wider text-zinc-500 mb-1">Nabywca</h4>
            <div className="text-sm">
              <div className="font-bold">{invoice.buyer.name}</div>
              <div className="text-zinc-600">
                {invoice.buyer.no_nip ? "Osoba prywatna" : `NIP: ${invoice.buyer.nip}`}
              </div>
              <div className="text-zinc-600">{invoice.buyer.street_hn}</div>
              <div className="text-zinc-600">{invoice.buyer.zip} {invoice.buyer.city}, {invoice.buyer.country}</div>
            </div>
          </div>
          <div>
            <h4 className="font-bold text-xs uppercase tracking-wider text-zinc-500 mb-1">Daty i płatność</h4>
            <div className="text-sm space-y-0.5">
              <div>Wystawienia: <span className="font-mono">{invoice.issue_date}</span></div>
              <div>Sprzedaży: <span className="font-mono">{invoice.sale_date}</span></div>
              {invoice.payment_due_date && (
                <div>Termin: <span className="font-mono">{invoice.payment_due_date}</span></div>
              )}
              <div>Forma: {invoice.payment_method}</div>
              {invoice.bank_account && (
                <div className="text-xs font-mono text-zinc-600">Konto: {invoice.bank_account}</div>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-100 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2">Nazwa</th>
                <th className="text-right px-3 py-2">Ilość</th>
                <th className="text-right px-3 py-2">Cena netto</th>
                <th className="text-left px-3 py-2">VAT</th>
                <th className="text-right px-3 py-2">Netto</th>
                <th className="text-right px-3 py-2">Brutto</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items.map((it, i) => {
                const netto = Number(it.quantity) * Number(it.unit_price_net);
                const brutto = /^\d+$/.test(String(it.vat_rate))
                  ? netto * (1 + parseInt(it.vat_rate, 10) / 100)
                  : netto;
                return (
                  <tr key={i} className="border-b border-zinc-100">
                    <td className="px-3 py-2">{it.name}</td>
                    <td className="px-3 py-2 text-right font-mono">{it.quantity} {it.unit}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(it.unit_price_net).toFixed(2)}</td>
                    <td className="px-3 py-2">{/^\d/.test(it.vat_rate) ? `${it.vat_rate}%` : it.vat_rate}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{netto.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{brutto.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-zinc-50">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right text-xs uppercase font-mono text-zinc-500">Razem</td>
                <td className="px-3 py-2 text-right font-mono font-bold">{invoice.total_net.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono font-black text-base">
                  {invoice.total_gross.toFixed(2)} {invoice.currency}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {invoice.notes && (
          <div className="text-xs text-zinc-600 border-l-4 border-zinc-300 pl-3">
            {invoice.notes}
          </div>
        )}
      </div>
    </div>
  );
}
